import {
	type ChangeEvent,
	type RefObject,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react';
import { FaXmark } from 'react-icons/fa6';
import { IoFlash } from 'react-icons/io5';
import { MdFlipCameraIos } from 'react-icons/md';

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@hubmaster/ui/select';
import { Toggle } from '@hubmaster/ui/toggle';
import { Portal } from '@radix-ui/react-portal';
import {
	type CameraDevice,
	Html5Qrcode,
	Html5QrcodeSupportedFormats,
} from 'html5-qrcode';

import beepSoundNotFound from './bip-notfound.mp3';
import beepSound from './bip-scanner.mp3';

export const beepSounds = {
	read: beepSound,
	notFound: beepSoundNotFound,
};

interface BarcodeScannerProps {
	onRead: (value: string) => void;
	setActiveScanner: (value: any) => void;
	activeScanner: boolean;
	type: 'barcode' | 'qrcode';
}

let beep: HTMLAudioElement | null = null;

if (typeof window !== 'undefined') {
	beep = new Audio(beepSound);
}

function soundScannerSuccess() {
	if (beep) {
		beep.play();
	}
	if (typeof navigator !== 'undefined' && navigator.vibrate) {
		navigator.vibrate([100]);
	}
}

function getBestCameraForBarcode(
	cameras: CameraDevice[],
): CameraDevice | undefined {
	const score = (label: string) => {
		const l = label.toLowerCase();
		// Evitar: frontal e ultrawide (focal curta — ruim para barcodes)
		if (
			l.includes('front') ||
			l.includes('selfie') ||
			l.includes('user') ||
			l.includes('frontal') ||
			l.includes('wide') ||
			l.includes('ultra')
		)
			return -1;
		// Melhor: telefoto (focal longa — ótimo para barcodes)
		if (l.includes('tele') || l.includes('zoom')) return 3;
		// Bom: câmera traseira principal
		if (
			l.includes('back') ||
			l.includes('rear') ||
			l.includes('traseiro') ||
			l.includes('environment')
		)
			return 2;
		// Desconhecida: provavelmente traseira principal em labels vazios (iOS)
		return 1;
	};
	return [...cameras].sort((a, b) => score(b.label) - score(a.label))[0];
}

export function BarcodeScanner({
	onRead,
	activeScanner,
	setActiveScanner,
	type: _type,
}: BarcodeScannerProps) {
	const [devices, setDevices] = useState<CameraDevice[]>([]);
	const [cameraActiveId, setCameraActiveId] = useState<string | undefined>();
	const [isScanning, setIsScanning] = useState<boolean>(false);
	// Mantém o overlay visível enquanto um start está em andamento. Esconder o
	// container (0px) no meio do start quebra o html5-qrcode (erro de qrbox e a
	// instância trava com "Cannot clear while scan is ongoing").
	const [isStarting, setIsStarting] = useState<boolean>(false);
	const scanRef = useRef<Html5Qrcode | null>(null);

	// Evita corrida de start() duplicado do html5-qrcode: ao abrir, startScan roda
	// uma vez (cameraActiveId ainda indefinido) e, quando getCameras resolve e
	// define a câmera, o efeito dispara startScan de novo. Dois start() concorrentes
	// na mesma instância deixavam a câmera ligada com a tela preta.
	const startingRef = useRef(false);
	const pendingRestartRef = useRef(false);
	// "Últimos valores" para os callbacks assíncronos (o start demora a resolver).
	const activeScannerRef = useRef(activeScanner);
	activeScannerRef.current = activeScanner;
	const cameraActiveIdRef = useRef(cameraActiveId);
	cameraActiveIdRef.current = cameraActiveId;

	// Id ÚNICO por instância. O id fixo antigo colidia quando havia mais de um
	// Scanner montado na mesma tela (ex: no mixirica, o botão da busca + o FAB):
	// getElementById pegava o primeiro container (escondido) e o html5-qrcode
	// jogava o vídeo lá, deixando o overlay visível preto com a câmera ligada.
	const scannerElementId = `scanner_barcode_${useId().replace(/[:]/g, '')}`;

	// Carrega câmeras disponíveis para o seletor, sem tocar no html5-qrcode ainda
	useEffect(() => {
		Html5Qrcode.getCameras().then((cameras) => {
			setDevices(cameras);
			const camerasId = cameras.map((camera) => camera.id);
			const cameraDefault = localStorage.getItem('cameraActiveId');
			if (cameraDefault && camerasId.includes(cameraDefault)) {
				setCameraActiveId(cameraDefault);
			} else {
				const best = getBestCameraForBarcode(cameras);
				if (best) setCameraActiveId(best.id);
			}
		});
	}, []);

	useEffect(() => {
		if (true === activeScanner) {
			startScan();
		} else if (!startingRef.current) {
			// Fecha só quando NÃO há start em andamento. Se houver, o finally do
			// start cuida de parar quando ele terminar (senão a gente esconderia o
			// container no meio do start e quebraria o html5-qrcode).
			void stopAndClear();
		}
	}, [cameraActiveId, activeScanner]);

	// Solta a câmera direto pelas tracks do <video> e remove o elemento. Rede de
	// segurança: se o html5-qrcode travou (stop/clear falharam), o stream fica
	// ativo e a câmera presa ("em uso"), e a próxima abertura vem com a tela preta.
	// No caminho normal o clear já removeu o vídeo, então isto vira no-op.
	function releaseCameraTracks() {
		try {
			const el = document.getElementById(scannerElementId);
			if (!el) return;
			el.querySelectorAll('video').forEach((video) => {
				const stream = video.srcObject as MediaStream | null;
				stream?.getTracks().forEach((track) => track.stop());
				video.srcObject = null;
				video.remove();
			});
		} catch {
			/* melhor esforço */
		}
	}

	// Para e limpa de forma defensiva. Se o clear falhar ("Cannot clear while scan
	// is ongoing"), a instância travou: joga fora para a próxima abertura criar uma
	// limpa. Em qualquer caso libera a câmera.
	async function stopAndClear() {
		// Esconde JÁ os controles (Flash/Zoom): eles consultam a câmera no render,
		// e se renderizarem durante o stop o html5-qrcode lança "The RenderedCamera
		// has already been closed".
		setIsScanning(false);
		const scanner = scanRef.current;
		if (scanner) {
			try {
				if (scanner.isScanning) await scanner.stop();
			} catch {
				/* stop pode rejeitar se o start falhou no meio */
			}
			try {
				scanner.clear();
			} catch {
				scanRef.current = null;
			}
		}
		releaseCameraTracks();
	}

	// Inicializa o html5-qrcode na primeira abertura — o Portal já garantiu
	// que #scanner_barcode existe no DOM antes desta função ser chamada.
	async function initializeScanner(): Promise<boolean> {
		if (scanRef.current) return true;
		const el = document.getElementById(scannerElementId);
		if (!el) return false;
		// Limpa restos de uma sessão anterior travada (vídeo/stream vazado) antes de
		// criar a instância nova, senão a câmera fica presa e a tela vem preta.
		releaseCameraTracks();
		scanRef.current = new Html5Qrcode(scannerElementId, {
			// Usa a BarcodeDetector API nativa do browser quando disponível (muito mais rápida)
			useBarCodeDetectorIfSupported: true,
			verbose: false,
			// Todos os formatos comuns — QR code + barras — numa única passagem
			formatsToSupport: [
				Html5QrcodeSupportedFormats.EAN_13,
				Html5QrcodeSupportedFormats.EAN_8,
				Html5QrcodeSupportedFormats.CODE_128,
				Html5QrcodeSupportedFormats.CODE_39,
				Html5QrcodeSupportedFormats.UPC_A,
				Html5QrcodeSupportedFormats.UPC_E,
				Html5QrcodeSupportedFormats.ITF,
				Html5QrcodeSupportedFormats.CODABAR,
				Html5QrcodeSupportedFormats.QR_CODE,
				Html5QrcodeSupportedFormats.DATA_MATRIX,
				Html5QrcodeSupportedFormats.PDF_417,
				Html5QrcodeSupportedFormats.AZTEC,
			],
		});
		return true;
	}

	async function startScan() {
		// Serializa: se um start já está em andamento, marca para reiniciar depois
		// (já com a câmera mais recente) em vez de chamar start() concorrente, que é
		// o que deixava a câmera ligada com a tela preta.
		if (startingRef.current) {
			pendingRestartRef.current = true;
			return;
		}
		startingRef.current = true;
		// Mantém o overlay montado/dimensionado durante todo o start, mesmo que
		// fechem no meio: o container só pode ser escondido depois de parar.
		setIsStarting(true);
		try {
			const ready = await initializeScanner();
			if (!ready) {
				setActiveScanner('popup');
				return;
			}

			if (scanRef.current!.isScanning) {
				await stopAndClear();
			}

			const configScan: MediaTrackConstraints = cameraActiveIdRef.current
				? { deviceId: cameraActiveIdRef.current }
				: { facingMode: 'environment' };

			await scanRef.current!.start(
				configScan,
				{
					// 25 fps — mais tentativas por segundo sem custo alto (BarcodeDetector é nativo)
					fps: 25,
					aspectRatio: window.innerHeight / window.innerWidth,
					// Cobre ~75% da largura e ~50% da altura: lê barcodes horizontais e QR
					// codes. Piso de 50px: o html5-qrcode recusa qrbox menor que isso.
					qrbox: (w, h) => ({
						width: Math.max(50, Math.round(Math.min(w * 0.75, 320))),
						height: Math.max(50, Math.round(Math.min(h * 0.5, 280))),
					}),
				},
				(data) => {
					onRead(data);
					soundScannerSuccess();
					scanRef.current?.stop().then(() => {
						setIsScanning(false);
						setActiveScanner(false);
					});
				},
				() => setIsScanning(!!scanRef.current?.isScanning),
			);
			setIsScanning(!!scanRef.current?.isScanning);
		} catch (e) {
			// Falhou no meio (ex: fecharam e o container sumiu): descarta a instância
			// para a próxima abertura começar limpa.
			await stopAndClear();
			scanRef.current = null;
			if (activeScannerRef.current) setActiveScanner('popup');
			console.error(e);
		} finally {
			startingRef.current = false;
			setIsStarting(false);
			// A câmera mudou (ou pediram restart) durante o start: reinicia agora,
			// lendo o cameraActiveId atual, e só se o scanner ainda estiver aberto.
			if (pendingRestartRef.current) {
				pendingRestartRef.current = false;
				if (activeScannerRef.current) {
					startScan();
					return;
				}
			}
			// Fecharam o scanner enquanto ele iniciava: agora que terminou, para e
			// limpa (o efeito de fechar foi pulado por causa do start em andamento).
			if (!activeScannerRef.current) void stopAndClear();
		}
	}

	function updateCameraActiveId(id: string) {
		setCameraActiveId(id);
		localStorage.setItem('cameraActiveId', id);
		localStorage.removeItem('activeFlash');
		localStorage.removeItem('zoom');
	}

	// Portal renderiza no document.body — fora de qualquer overflow:hidden do app.
	// Isso resolve o iOS Safari onde fixed dentro de overflow:hidden não intercepta toques.
	// Toolbar abaixo do vídeo para não sobrepor o <video> (camada nativa no iOS).
	return (
		<Portal>
			<div
				className={`fixed inset-0 z-[9998] flex flex-col bg-black ${activeScanner || isStarting ? '' : 'hidden'}`}
			>
				{/* html5-qrcode popula este div com o <video> */}
				<div id={scannerElementId} />

				{/* Loading enquanto a câmera não entrou no ar (abrindo/iniciando). Some
				    assim que o scan começa (o vídeo aparece). */}
				{(activeScanner || isStarting) && !isScanning && (
					<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3">
						<div className="border-zinc-500! border-t-white! h-9 w-9 animate-spin rounded-full border-4" />
						<p className="text-sm text-white/80">Abrindo câmera...</p>
					</div>
				)}

				{/* Controles abaixo do vídeo — nunca sobrepostos */}
				<div className="absolute inset-x-0 top-4 flex shrink-0 items-center justify-between px-4 py-3">
					<div className="flex gap-2">
						{isScanning && scanRef.current?.isScanning && (
							<Select
								onValueChange={(value: any) => updateCameraActiveId(value)}
								defaultValue={cameraActiveId}
							>
								<SelectTrigger
									className="h-10 w-10 justify-center rounded-full bg-white/10 px-1 py-1"
									showIcon={false}
								>
									<MdFlipCameraIos size={20} className="text-white" />
								</SelectTrigger>
								<SelectContent className="bg-background z-[9999]">
									{devices.map((device) => (
										<SelectItem key={device.id} value={device.id}>
											{device.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						{isScanning && scanRef.current?.isScanning && (
							<Flash scanRef={scanRef} />
						)}
					</div>

					{isScanning && scanRef.current?.isScanning && (
						<Zoom scanRef={scanRef} />
					)}

					<button
						className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white"
						onClick={() => setActiveScanner(false)}
					>
						<FaXmark size={18} />
					</button>
				</div>
			</div>
		</Portal>
	);
}

function Zoom({ scanRef }: { scanRef: RefObject<Html5Qrcode | null> }) {
	let supportZoom = false;
	try {
		supportZoom = Boolean(
			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.zoomFeature()
				.isSupported(),
		);
	} catch {
		// Câmera já fechada (fechamento rápido): sem controle de zoom.
		supportZoom = false;
	}
	const [zoom, setZoom] = useState(0);

	useEffect(() => {
		if (!supportZoom) return;
		const zoom = localStorage.getItem('zoom');
		if (zoom) {
			setZoom(parseFloat(zoom));

			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.zoomFeature()
				.apply(parseFloat(zoom.toString()));
		}
	}, []);

	function handleZoomChange(e: ChangeEvent<HTMLInputElement>) {
		const zoom = parseFloat(e.currentTarget.value);

		if (!isNaN(zoom)) {
			setZoom(zoom);
			localStorage.setItem('zoom', zoom.toString());

			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.zoomFeature()
				.apply(parseFloat(e.currentTarget.value));
		}
	}

	if (!supportZoom) return null;

	return (
		<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 translate-y-28">
			<input
				onChange={handleZoomChange}
				value={zoom}
				type="range"
				min={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.min()}
				max={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.max()}
				step={scanRef.current
					?.getRunningTrackCameraCapabilities()
					.zoomFeature()
					.step()}
			/>
		</div>
	);
}

function Flash({ scanRef }: { scanRef: RefObject<Html5Qrcode | null> }) {
	let supportFlash = false;
	try {
		supportFlash = Boolean(
			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.torchFeature()
				.isSupported(),
		);
	} catch {
		// Câmera já fechada (fechamento rápido): sem controle de flash.
		supportFlash = false;
	}
	const [active, setActiveFlash] = useState(false);

	useEffect(() => {
		if (!supportFlash) return;
		const flash = localStorage.getItem('activeFlash');
		if (flash) {
			const active = flash == 'true';
			setActiveFlash(active);
			scanRef.current
				?.getRunningTrackCameraCapabilities()
				.torchFeature()
				.apply(active);
		}
	}, []);

	function handleToggleFlash(active: boolean) {
		setActiveFlash(active);
		localStorage.setItem('activeFlash', active.toString());
		scanRef.current
			?.getRunningTrackCameraCapabilities()
			.torchFeature()
			.apply(active);
	}

	if (!supportFlash) return null;

	return (
		<Toggle
			size={'lg'}
			pressed={active}
			className="h-10 w-10 rounded-full"
			onPressedChange={handleToggleFlash}
		>
			<IoFlash />
		</Toggle>
	);
}
